import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../helpers/Modal';
import { FaeKey } from '../../types';
import { capitalize } from '../../utils/capitalize';
import { SWORN_DATA, FAE_COLORS } from '../../gameData';
import { Check } from 'lucide-react';
import { Blessing } from '../../types';
import { GameEntityCard } from '../ui/GameEntityCard';
import { getIconPath } from '../../utils/iconPath';

interface CompendiumFaeModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialFae: FaeKey | null;
}

const BlessingSection = React.memo(({ 
    title, 
    blessings, 
    color 
}: { 
    title: string; 
    blessings: Blessing[] | undefined; 
    color: string; 
}) => {
    if (!blessings || blessings.length === 0) return null;
    return (
        <>
            <h4 className="text-base md:text-lg font-semibold text-white mb-1 mt-4 first:mt-0">{title}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {blessings.map(b => {
                    const desc = 'description' in b ? b.description : 'effect' in b ? b.effect : '';
                    const subText = 'type' in b && b.type !== 'standard' && b.type !== 'duo' && b.type !== 'round_table' && b.type !== 'shrine' ? `(${b.type})` : 
                                    'partner' in b ? `(${b.partner})` : '';
                    
                    return (
                        <div key={b.name} className="p-2 bg-slate-700 rounded-md">
                            <p className="font-bold text-xs md:text-sm leading-tight" style={{ color }}>
                                {b.name}
                                {subText && <span className="text-[10px] md:text-xs text-slate-400 ml-1">{subText}</span>}
                            </p>
                            <p className="text-[10px] md:text-xs text-slate-300 leading-tight mt-0.5">{desc}</p>
                        </div>
                    );
                })}
            </div>
        </>
    );
});

const FaeDetails = React.memo(({ selectedFae }: { selectedFae: FaeKey }) => {
    const faeData = SWORN_DATA.fae_lords[selectedFae];
    const color = FAE_COLORS[selectedFae].hex;

    return (
        <div 
            className="p-4 bg-slate-800 rounded-lg border-2 max-h-[50vh] overflow-y-auto"
            style={{ borderColor: color }}
        >
            <h3 className="text-xl md:text-2xl font-bold mb-2" style={{ color: color }}>{capitalize(selectedFae)}</h3>
            <BlessingSection title="Ability Blessings" blessings={faeData.ability_blessings} color={color} />
            <BlessingSection title="Standard Blessings" blessings={faeData.standard_blessings} color={color} />
            <BlessingSection title="Duo Blessings" blessings={faeData.duo_blessings} color={color} />
            <BlessingSection title="Round Table Blessings" blessings={faeData.round_table_blessings} color={color} />
            <BlessingSection title="Shrine Blessings" blessings={faeData.shrine_blessings} color={color} />
        </div>
    );
});

const CompendiumFaeModal: React.FC<CompendiumFaeModalProps> = ({ isOpen, onClose, initialFae }) => {
    const faeKeys = Object.keys(SWORN_DATA.fae_lords) as FaeKey[];
    const [selectedFae, setSelectedFae] = useState<FaeKey | null>(initialFae);

    useEffect(() => {
        if (isOpen) {
            setSelectedFae(initialFae);
        }
    }, [isOpen, initialFae]);

    const handleSelectFae = useCallback((key: FaeKey) => setSelectedFae(key), []);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Build Index: Fae Lords" size="4xl">
            <div className="flex flex-wrap gap-4 justify-center mb-6">
                {faeKeys.map(key => (
                    <GameEntityCard
                        key={key}
                        id={key}
                        title={capitalize(key)}
                        iconPath={getIconPath('fae', key)}
                        isSelected={selectedFae === key}
                        onClick={(id) => handleSelectFae(id as FaeKey)}
                        imageStyle={{
                            borderColor: FAE_COLORS[key].hex,
                            backgroundColor: `${FAE_COLORS[key].hex}33`,
                            padding: '0.5rem',
                            objectFit: 'contain'
                        }}
                    >
                        {selectedFae === key && (
                            <div className="absolute top-1 right-1 bg-green-600 rounded-full p-1 shadow-lg border border-white z-10">
                                <Check className="w-3 h-3 text-white" strokeWidth={3} />
                            </div>
                        )}
                    </GameEntityCard>
                ))}
            </div>
            {selectedFae && <FaeDetails selectedFae={selectedFae} />}
        </Modal>
    );
};

export default CompendiumFaeModal;